import CircularProgress from '@mui/material/CircularProgress';
import InputAdornment from '@mui/material/InputAdornment';
import TextField from '@mui/material/TextField';
import type { TextFieldProps } from '@mui/material/TextField';
import { FocusEvent } from 'react';
import {
	FieldPath,
	FieldValues,
	UseControllerProps,
	useController,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { displayError, getHelperText } from './functions';

export type TextFieldElementProps<
	TFieldValues extends FieldValues = FieldValues,
	TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = Pick<UseControllerProps<TFieldValues, TName>, 'name' | 'control'> &
	Omit<TextFieldProps, 'name' | 'value' | 'onChange' | 'ref' | 'error'> & {
		validation?: UseControllerProps<TFieldValues, TName>['rules'];
		isLoading?: boolean;
	};

export const ControlledTextField = <
	TFieldValues extends FieldValues = FieldValues,
	TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
	control,
	name,
	validation = {},
	required,
	disabled,
	type,
	onBlur: onTextFieldBlur,
	helperText,
	isLoading,
	slotProps,
	...rest
}: TextFieldElementProps<TFieldValues, TName>): React.JSX.Element => {
	const { t } = useTranslation();
	const { inputLabel, input, select, ...restSlotProps } = slotProps ?? {};
	const inputSlotProps = typeof input === 'function' ? undefined : input;
	const selectSlotProps = typeof select === 'function' ? undefined : select;

	if (Boolean(validation.required) && !required) {
		required = true;
	}

	const {
		field: { onChange, onBlur, value, ref },
		fieldState: { error },
	} = useController<TFieldValues, TName>({
		name,
		control,
		rules: validation,
	});

	const handleBlur = (
		event: FocusEvent<HTMLInputElement | HTMLTextAreaElement>
	) => {
		onBlur();
		if (typeof onTextFieldBlur === 'function') {
			onTextFieldBlur(event);
		}
	};

	return (
		<TextField
			name={name}
			value={value ?? ''}
			onChange={onChange}
			onBlur={handleBlur}
			inputRef={ref}
			error={displayError(disabled, error)}
			helperText={getHelperText(t, disabled, error, helperText)}
			required={required}
			disabled={disabled || isLoading}
			type={type}
			slotProps={{
				...restSlotProps,
				select: {
					...selectSlotProps,
					...(isLoading && { IconComponent: () => null }),
				},
				input: {
					...inputSlotProps,
					endAdornment: isLoading ? (
						<InputAdornment position="end">
							<CircularProgress size={20} />
						</InputAdornment>
					) : (
						inputSlotProps?.endAdornment
					),
				},
				inputLabel: {
					shrink: value != null && value !== '',
					...inputLabel,
				},
			}}
			{...rest}
		/>
	);
};
